import React from 'react';
import {
  useTable,
  useGlobalFilter,
  useSortBy,
  usePagination,
} from 'react-table';

function GlobalFilter({ preGlobalFilteredRows, globalFilter, setGlobalFilter }) {
  const count = preGlobalFilteredRows.length;

  return (
    <div className="mb-4 d-flex align-items-center">
      <div className="position-relative w-100">
        <input
          type="text"
          className="form-control"
          value={globalFilter || ''}
          onChange={e => setGlobalFilter(e.target.value || undefined)}
          placeholder={`Search ${count} entries...`}
          style={{ 
            paddingLeft: '45px', 
            borderRadius: '15px', 
            border: 'none', 
            boxShadow: '0 5px 15px rgba(0,0,0,0.02)',
            backgroundColor: '#fff'
          }}
        />
        <div className="position-absolute" style={{ left: '15px', top: '50%', transform: 'translateY(-50%)', opacity: 0.4 }}>
          🔍
        </div>
      </div>
    </div>
  );
}

const AdminTable = ({ tableHeaders = [], tableData = [] }) => {
  const data = React.useMemo(
    () => (tableData.length ? tableData : []),
    [tableData] 
  );

  const columns = React.useMemo(
    () => (tableHeaders.length ? tableHeaders : []),
    [tableHeaders] 
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    page,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    setPageSize,
  } = useTable(
    { columns, data, initialState: { pageSize: 10 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  return (
    <div className="fade-in">
      <GlobalFilter
        preGlobalFilteredRows={preGlobalFilteredRows}
        globalFilter={state.globalFilter}
        setGlobalFilter={setGlobalFilter}
      />

      <div className="card border-0 overflow-hidden" style={{ borderRadius: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.03)' }}>
        <div className="table-responsive">
          <table {...getTableProps()} className="table mb-0" style={{ backgroundColor: '#fff' }}>
            <thead style={{ backgroundColor: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
              {headerGroups.map((headerGroup, index) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column, colIndex) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={colIndex}
                      className="text-uppercase small font-weight-bold p-4 text-muted border-0"
                    >
                      <div className="d-flex align-items-center justify-content-center">
                        {column.render('Header')}
                        <span className="ml-2" style={{ fontSize: '10px' }}>
                          {column.isSorted
                            ? column.isSortedDesc
                              ? '▼'
                              : '▲'
                            : '⇅'}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row, i) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} key={i} className="transition" style={{ borderBottom: '1px solid #f8f8f8' }}>
                    {row.cells.map((cell, cellIndex) => (
                      <td
                        {...cell.getCellProps()}
                        key={cellIndex}
                        className="p-4 align-middle text-center"
                        style={{ color: '#444', fontWeight: '400' }}
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      <div className="d-flex justify-content-between align-items-center mt-4 px-2">
        <div className="small text-muted font-weight-medium">
          Showing <span className="text-dark font-weight-bold">{page.length}</span> of <span className="text-dark font-weight-bold">{data.length}</span> results
        </div>
        
        <div className="d-flex align-items-center gap-3">
          <div className="d-flex bg-white rounded-pill shadow-sm p-1">
            <button
              className="btn btn-sm px-4 rounded-pill border-0 transition"
              style={{ 
                backgroundColor: !canPreviousPage ? '#f9f9f9' : 'transparent',
                color: !canPreviousPage ? '#ccc' : 'var(--primary-gold)',
                fontWeight: '600'
              }}
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
            >
              Previous
            </button>
            <button
              className="btn btn-sm px-4 rounded-pill border-0 transition"
              style={{ 
                backgroundColor: !canNextPage ? '#f9f9f9' : 'transparent',
                color: !canNextPage ? '#ccc' : 'var(--primary-gold)',
                fontWeight: '600'
              }}
              onClick={() => nextPage()}
              disabled={!canNextPage}
            >
              Next
            </button>
          </div>
          
          <select 
            className="form-control-sm border-0 shadow-sm rounded-pill px-3" 
            value={state.pageSize} 
            onChange={e => setPageSize(Number(e.target.value))}
            style={{ height: '35px', outline: 'none', cursor: 'pointer' }}
          >
            {[5, 10, 20, 50].map(size => (
              <option key={size} value={size}>Show {size}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default AdminTable;
